import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useCopy } from "../framework";
import { ArrowLinkLabel, EmptyState, LoadingState, PageHeader, SectionHeading } from "../components/ui";
import { CopyAssetButton, copyableFormats, useAssetManifest } from "../downloadsManifest";
import { assetHref } from "../routerMount";

/**
 * The downloads page renders the generated site-safe asset manifest. The
 * manifest and every file it lists are produced by
 * tooling/generate-site-assets.ts from the audience-isolated site projection;
 * this page adds no content of its own and never fabricates an asset row.
 *
 * copy-register: commercial
 */

const groupOrder = [
  "authoring-kit",
  "authoring-guide",
  "authoring-prompt",
  "authoring-skill",
  "framework-brief",
  "workblock-starter",
  "workblock-design-prompt",
  "module-design-brief",
  "agent-charter-brief",
  "outcome-tool-recipe",
  "design-doctrine",
  "design-component",
  "integration-recipe",
  "entity-schema",
  "data-dictionary",
  "assurance-report",
];

function formatCount(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

export function DownloadsPage() {
  const copy = useCopy();
  /* /downloads/:assetId is each asset's canonical address. */
  const { assetId } = useParams();
  const { manifest, failed } = useAssetManifest();

  useEffect(() => {
    if (!assetId || !manifest) return;
    requestAnimationFrame(() =>
      document.getElementById(`asset-${assetId}`)?.scrollIntoView({ block: "start" }),
    );
  }, [assetId, manifest]);

  return (
    <div className="apf-page apf-register-marketing apf-downloads-page">
      <PageHeader
        eyebrow={copy("page.downloads.eyebrow")}
        title={copy("page.downloads.headline")}
        description={copy("page.downloads.body")}
        meta={manifest ? formatCount(copy("page.downloads.count"), manifest.assetCount) : undefined}
      />
      {failed ? <EmptyState title={copy("page.downloads.error")} description="" /> : null}
      {!failed && !manifest ? <LoadingState /> : null}
      {manifest
        ? groupOrder.map((assetClass) => {
            const assets = manifest.assets.filter((asset) => asset.assetClass === assetClass);
            if (!assets.length) return null;
            return (
              <section key={assetClass} className="apf-downloads-group" aria-labelledby={`downloads-${assetClass}`}>
                <SectionHeading
                  id={`downloads-${assetClass}`}
                  title={copy(`page.downloads.group.${assetClass}`)}
                  description={copy(`page.downloads.group.${assetClass}.description`)}
                />
                <ul className="apf-downloads-list">
                  {assets.map((asset) => (
                    <li
                      key={asset.assetId}
                      className={`apf-downloads-row${asset.assetId === assetId ? " apf-downloads-row--addressed" : ""}`}
                      id={`asset-${asset.assetId}`}
                      aria-current={asset.assetId === assetId ? "true" : undefined}
                    >
                      <div className="apf-downloads-row__identity">
                        <h3>{asset.title}</h3>
                        <p className="apf-downloads-row__facts">
                          <span>{copy("page.downloads.version-label")} {asset.version}</span>
                          <span>{copy("page.downloads.proof-label")}: {asset.proof.label}</span>
                          <span>{copy("page.downloads.licence-label")}: {asset.licence.label}</span>
                        </p>
                      </div>
                      <div className="apf-downloads-row__actions">
                        {asset.formats.map((format) => (
                          <span key={format.path} className="apf-downloads-row__format">
                            <a className="apf-button apf-button--quiet" href={assetHref(format.path)} download>
                              {copy("page.downloads.download")} {format.format}
                            </a>
                            {copyableFormats.has(format.format) ? (
                              <CopyAssetButton path={format.path} copy={copy} />
                            ) : null}
                          </span>
                        ))}
                        <Link to={asset.canonicalRoute}>
                          <ArrowLinkLabel>{copy("page.downloads.route-label")}</ArrowLinkLabel>
                        </Link>
                      </div>
                      <p className="apf-downloads-row__hash">
                        {copy("page.downloads.hash-label")}{" "}
                        <code>{asset.formats[0]?.sha256.slice(0, 16)}</code>
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })
        : null}
    </div>
  );
}
